import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import S from './Widget.module.scss';
import WidgetHeaderControls from './WidgetHeaderControls';
import {
  DEFAULT_WIDGET_OPTIONS,
  resolveWidgetOptions,
} from './widgetOptions';
import dashboardPreviewImage from '../../../ressources/parking.PNG';

const WidgetTableauDeBord = ({
  dashboards = [],
  vuesFavorites = [],
  onVueClick,
  graphesFavoris = [],
  onGrapheClick,
  availableWidgets,
  initialWidget = null,
  sizeVariant = 'large',
  headerContent = null,
  showHeader = true,
}) => {
  const hasDashboards = dashboards && dashboards.length > 0;
  const hasVue = vuesFavorites && vuesFavorites.length > 0;
  const hasGraphes = graphesFavoris && graphesFavoris.length > 0;
  const widgetOptions = useMemo(
    () => resolveWidgetOptions(availableWidgets),
    [availableWidgets]
  );
  const dashboardName =
    dashboards[0]?.dashboard_name || 'Consommation de carburant';
  const dashboardImageSrc = dashboards[0]?.image_url || dashboardPreviewImage;
  const topBars = [72, 58, 80, 64, 92, 65, 78];
  const bottomBars = [55, 42, 63, 51, 70, 57, 48, 62, 59, 40];
  const firstVue = hasVue ? vuesFavorites[0] : null;
  const firstGraphe = hasGraphes ? graphesFavoris[0] : null;

  const pickInitialWidget = () => {
    const allowedValues = widgetOptions.map((option) => option.value);
    if (initialWidget && allowedValues.includes(initialWidget)) {
      return initialWidget;
    }
    if (hasDashboards && allowedValues.includes('dashboard')) {
      return 'dashboard';
    }
    if (hasVue && allowedValues.includes('plan')) {
      return 'plan';
    }
    if (hasGraphes && allowedValues.includes('graphe')) {
      return 'graphe';
    }
    return widgetOptions[0]?.value || DEFAULT_WIDGET_OPTIONS[0].value;
  };

  const [selectedWidget, setSelectedWidget] = useState(() => pickInitialWidget());
  const currentWidget =
    widgetOptions.find((option) => option.value === selectedWidget) ||
    widgetOptions[0] ||
    DEFAULT_WIDGET_OPTIONS[0];

  useEffect(() => {
    if (!widgetOptions.some((option) => option.value === selectedWidget)) {
      const fallback = widgetOptions[0]?.value || DEFAULT_WIDGET_OPTIONS[0].value;
      setSelectedWidget(fallback);
    }
  }, [selectedWidget, widgetOptions]);

  const renderDashboardContent = () => (
    <div className={S.dashboardPreviewContent}>
      <img
        src={dashboardImageSrc}
        alt={dashboardName}
        className={S.dashboardPreviewImage}
      />
    </div>
  );

  const renderPlanContent = () => (
    <div className={S.widgetContent}>
      {hasVue ? (
        <div className={S.planContainer}>
          <div
            className={S.planPreview}
            onClick={() => onVueClick && firstVue && onVueClick(firstVue)}
          >
            <img
              src={firstVue?.image_url || 'https://tile.openstreetmap.org/5/15/11.png'}
              alt={firstVue?.view_name || 'plan'}
              className={S.planImage}
            />
          </div>
        </div>
      ) : (
        <div className={S.emptyState}>
          <div className={S.mapPlaceholder}>
            <img
              src="https://tile.openstreetmap.org/5/15/11.png"
              alt="map placeholder"
              className={S.planImage}
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderGrapheContent = () => (
    <div className={S.widgetContent}>
      {hasGraphes ? (
        <div className={S.grapheContainer}>
          <div className={S.grapheTitle}>
            {firstGraphe?.graph_name || 'Graphe'}
          </div>
          <div className={S.graphePreview}>
            <div className={S.chartStack}>
              <div className={S.barChart}>
                {topBars.map((height, index) => (
                  <div
                    key={`top-${index}`}
                    className={S.bar}
                    style={{ height: `${height}%` }}
                  />
                ))}
              </div>
              <div className={[S.barChart, S.barChartSmall].join(' ')}>
                {bottomBars.map((height, index) => (
                  <div
                    key={`bottom-${index}`}
                    className={S.bar}
                    style={{ height: `${height}%` }}
                  />
                ))}
              </div>
            </div>
          </div>
          {firstGraphe?.graph_id && (
            <Link
              to={`/graphe/${firstGraphe.graph_id}`}
              className={S.widgetLink}
              onClick={() => onGrapheClick && onGrapheClick(firstGraphe)}
            >
              {checkMot('voir_plus')}
            </Link>
          )}
        </div>
      ) : (
        <div className={S.emptyState}>
          <div className={S.barChart}>
            {[40, 60, 30, 70, 50, 80, 45, 65, 35, 55, 75, 40].map((height, index) => (
              <div
                key={index}
                className={[S.bar, S.barEmpty].join(' ')}
                style={{ height: `${height}%` }}
              />
            ))}
          </div>
          <p className={S.emptyText}>{checkMot('aucun_graphe_favori')}</p>
        </div>
      )}
    </div>
  );

  const renderSelectedContent = () => {
    if (selectedWidget === 'plan') {
      return renderPlanContent();
    }
    if (selectedWidget === 'graphe') {
      return renderGrapheContent();
    }
    return hasDashboards ? (
      renderDashboardContent()
    ) : (
      <div className={S.emptyStateSimple}>
        <p className={S.emptyText}>{checkMot('aucun_tableau_de_bord_favori')}</p>
      </div>
    );
  };
  
  const widgetSizeClass =
    sizeVariant === 'medium'
      ? S.widgetMedium
      : sizeVariant === 'small'
      ? S.widgetSmall
      : S.widgetLarge;

  const resolvedHeader =
    headerContent ||
    (showHeader ? (
      <WidgetHeaderControls
        titleKey={currentWidget.labelKey}
        selectOptions={widgetOptions}
        selectedValue={selectedWidget}
        onSelectChange={setSelectedWidget}
      />
    ) : null);

  return (
    <div className={[S.widget, widgetSizeClass, S.widgetDashboard].join(' ')}>
      {resolvedHeader}
      <div className={S.dashboardWidgetContent}>{renderSelectedContent()}</div>
    </div>
  );
};

export default WidgetTableauDeBord;


